/* The whole archive and the next step (21 October 2026, with the Avalon ladder).
   - [data-av-archive]  the row under every edition's buy box: the whole archive's price today, the regular total
     one by one, the saving, and one button that adds every edition not yet in the cart (with the Avalon month and
     its free-month code when the page's Edition + Avalon option is chosen), then opens the cart.
   - [data-av-next]     the one line under the items in the cart: with one to three editions, what the next one
     costs; with four or more, the whole archive.
   - AvalonArchive.take is also used by the collection page's archive tile.
   Every number is Shopify's own answer for that exact set of editions (assets/avalon-live-pricing.js asks it, in
   the visitor's currency). Nothing here knows the ladder: change a discount in Shopify and every line follows. */
(() => {
  'use strict';
  if (window.AvalonArchive) { window.AvalonArchive.init(); return; }
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];
  const NTH = ['', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth'];
  const word = (n) => WORDS[n] || String(n);
  const nth = (n) => NTH[n] || `${n}th`;
  const pricing = () => window.AvalonLivePricing;
  /* Amounts drawn the way Liquid draws them, from the one amount the snippet had Liquid format (cfg.moneySample, read
     by avalon-live-pricing.js). Without it, the browser's currency format; either way whole amounts lose their
     ".00": "$333", "Rp 6.091.548,66". */
  const moneyFor = (cfg) => {
    const api = pricing();
    const like = api && api.moneyLike ? api.moneyLike(cfg && cfg.moneySample) : null;
    if (like) return like;
    return (amount, currency) => {
      const text = api ? api.money(amount, currency) : String(amount);
      return text.replace(/[.,]00(?=\D*$)/, '');
    };
  };
  /* Outside the dollar every total is shown rounded up the way Shopify rounds that currency's single prices, and what
     is saved rounded down: the cart charges the exact amount, never more than the line said. The rule comes with
     Shopify's answer (avalon-live-pricing.js, 27 Oct 2026); an answer without one is shown exactly. */
  const EXACT = { on: false, unit: 0, up: (x) => x, upTo: (x) => x, down: (x) => x, near: (x) => x, fits: () => true };
  const rule = (q) => (q && q.rounding) || EXACT;
  const config = (el) => {
    try {
      return JSON.parse(el.querySelector('[data-av-archive-config]').textContent);
    } catch (err) {
      return null;
    }
  };

  const request = async (path, body) => {
    const res = await fetch(`${root}${path}`, body
      ? { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
      : { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    const said = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error((said && (said.description || said.message)) || 'Please check your cart and try again.');
      if (said && typeof said.description === 'string') err.said = said.description;
      throw err;
    }
    return said;
  };
  const readCart = () => request('cart.js');

  /* The editions in the cart (bought once, any copy, a Mirror's gift copy included), by variant. */
  const editionLines = (cart, cfg) => {
    const ids = new Set((cfg.editions || []).map((e) => e.id));
    const held = new Map();
    for (const item of cart.items || []) {
      if (!ids.has(item.variant_id) || item.selling_plan_allocation) continue;
      held.set(item.variant_id, (held.get(item.variant_id) || 0) + item.quantity);
    }
    return [...held].map(([id, quantity]) => ({ id, quantity }));
  };
  /* Every line of the cart as Shopify would price it again: by variant and subscription plan. */
  const cartLines = (cart) => {
    const grouped = new Map();
    for (const item of cart.items || []) {
      const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id;
      const key = `${item.variant_id}:${plan || ''}`;
      const seen = grouped.get(key);
      if (seen) seen.quantity += item.quantity;
      else grouped.set(key, { id: item.variant_id, quantity: item.quantity, ...(plan ? { selling_plan: plan } : {}) });
    }
    return [...grouped.values()];
  };
  const missingOf = (cart, cfg) => {
    const have = new Set(editionLines(cart, cfg).map((line) => line.id));
    return (cfg.editions || []).filter((e) => !have.has(e.id));
  };
  const isMembership = (item, cfg) =>
    item.variant_id === cfg.subId || item.handle === 'avalon' || item.handle === 'the-avalon-letters-yearly-subscription';
  const sameCode = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  const attribution = (offer, el, kind, placement) => {
    try {
      return (window.ViuletUpsell && window.ViuletUpsell.properties(offer, el, kind, placement)) || {};
    } catch (err) {
      return {};
    }
  };

  /* Put every edition not yet in the cart in it, one each, then open the cart. With the month: the free-month code
     goes on the cart first and the month goes in with the editions (as the edition pages do it), so the month never
     sits in the cart without its code. A month Shopify does not make free comes back out, with the codes as they were. */
  const take = async (el, cfg, withMonth) => {
    const cart = await readCart();
    const missing = missingOf(cart, cfg);
    const placement = cfg.placement || 'product_page';
    const offer = withMonth ? 'avalon_membership_archive' : 'avalon_complete_archive';
    const kind = cfg.kind === 'cart' ? 'upsell' : 'primary';
    const add = missing.map((e) => ({ id: e.id, quantity: 1, properties: { ...attribution(offer, el, kind, placement) } }));
    const codes = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
    const wantsMonth = Boolean(withMonth && cfg.member && cfg.code && cfg.subId && cfg.subPlan) &&
      !(cart.items || []).some((item) => isMembership(item, cfg));
    if (wantsMonth) {
      if (!codes.some((code) => sameCode(code, cfg.code))) await request('cart/update.js', { discount: [...codes, cfg.code].join(',') });
      add.push({
        id: cfg.subId,
        quantity: 1,
        selling_plan: cfg.subPlan,
        properties: { ...attribution('avalon_complimentary_month', el, 'primary', placement), _avalon_complimentary: 'v1' },
      });
    }
    if (add.length > 0) await request('cart/add.js', { items: add });
    if (wantsMonth) {
      try {
        const after = await readCart();
        const month = (after.items || []).find((item) => item.variant_id === cfg.subId && item.properties && item.properties._avalon_complimentary === 'v1');
        if (month && Number(month.final_line_price) > 0) {
          await request('cart/change.js', { id: month.key, quantity: 0 });
          await request('cart/update.js', { discount: codes.join(',') });
        }
      } catch (err) {
        /* The cart page shows what is there. */
      }
    }
    window.location.assign(`${root}cart`);
  };

  const bindTake = (el, cfg, withMonth) => {
    const btn = el.querySelector('[data-av-archive-add]');
    const note = el.querySelector('[data-av-archive-note]');
    if (!btn || btn.dataset.avBound) return;
    btn.dataset.avBound = '1';
    btn.addEventListener('click', async () => {
      if (btn.disabled) return;
      const label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'One moment…';
      if (note) note.textContent = '';
      try {
        await take(el, cfg, withMonth());
      } catch (err) {
        btn.disabled = false;
        btn.textContent = label;
        if (note) note.textContent = (err && err.said) || 'Something went wrong. Please try again.';
      }
    });
    /* Back from the cart through the browser's page cache: give the button back. */
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      btn.disabled = false;
      if (btn.textContent === 'One moment…') btn.textContent = el.matches('[data-av-next]') ? 'Complete my archive' : 'Take the whole archive';
    });
  };

  /* The page's own Edition + Avalon option, when it is there and chosen. */
  const memberChosen = () => {
    const btn = document.querySelector('[data-gwb2-plan="member"]');
    return Boolean(btn && !btn.hidden && btn.getAttribute('aria-checked') === 'true');
  };

  const whenPricing = (fn) => {
    if (pricing() && pricing().quote) fn();
    else window.addEventListener('load', () => { if (pricing() && pricing().quote) fn(); }, { once: true });
  };
  const ask = async (lines, cfg, codes = []) => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        return await pricing().quote(lines, cfg.country, cfg.currency, codes);
      } catch (err) {
        if (attempt === 1) throw err;
        await new Promise((resolve) => setTimeout(resolve, 800));
      }
    }
    return null;
  };

  const initRow = (el) => {
    const cfg = config(el);
    if (!cfg || !(cfg.editions || []).length) return;
    const price = el.querySelector('[data-av-archive-price]');
    const regular = el.querySelector('[data-av-archive-regular]');
    const whisper = el.querySelector('[data-av-archive-whisper]');
    const month = el.querySelector('[data-av-archive-month]');
    const syncMonth = () => { if (month) month.hidden = !(cfg.member && memberChosen()); };
    syncMonth();
    document.addEventListener('click', (e) => {
      if (e.target && e.target.closest && e.target.closest('[data-gwb2-plan]')) setTimeout(syncMonth, 0);
    });
    document.addEventListener('keyup', () => setTimeout(syncMonth, 0));
    bindTake(el, cfg, () => cfg.member && memberChosen());
    /* A cart that already holds every edition has nothing to complete. */
    readCart().then((cart) => { if (missingOf(cart, cfg).length === 0) el.hidden = true; }).catch(() => {});
    const quoteNow = () => whenPricing(async () => {
      const money = moneyFor(cfg);
      try {
        const q = await ask(cfg.editions.map((e) => ({ id: e.id, quantity: 1 })), cfg);
        const r = rule(q);
        const total = r.upTo(q.total, q.original);
        const saved = r.down(Math.max(0, q.original - total));
        price.textContent = money(total, q.currency);
        if (saved > 0.005) {
          regular.textContent = `${money(q.original, q.currency)} one by one`;
          regular.hidden = false;
          whisper.textContent = `saves ${money(saved, q.currency)}, but that is not the point.`;
          whisper.hidden = false;
        } else {
          regular.hidden = true;
          whisper.hidden = true;
        }
        el.dataset.state = 'ready';
      } catch (err) {
        price.textContent = '';
        regular.textContent = 'its price follows in your cart';
        regular.hidden = false;
        whisper.hidden = true;
        el.dataset.state = 'unavailable';
      }
    });
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      quoteNow();
    };
    if ('IntersectionObserver' in window) {
      const seen = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        seen.disconnect();
        start();
      }, { rootMargin: '250px' });
      seen.observe(el);
      /* A page drawn in the background never says what is on screen: a row within two screens asks anyway. */
      setTimeout(() => {
        if (!started && el.getBoundingClientRect().top < (window.innerHeight || 800) * 2) {
          seen.disconnect();
          start();
        }
      }, 4000);
    } else {
      start();
    }
  };

  const initNext = (el) => {
    const cfg = config(el);
    if (!cfg || !(cfg.editions || []).length) return;
    const lead = el.querySelector('[data-av-next-lead]');
    const sub = el.querySelector('[data-av-next-sub]');
    bindTake(el, cfg, () => false);
    whenPricing(async () => {
      const money = moneyFor(cfg);
      try {
        const cart = await readCart();
        const have = editionLines(cart, cfg);
        const count = have.reduce((n, line) => n + line.quantity, 0);
        const missing = missingOf(cart, cfg);
        if (count < 1 || missing.length === 0) return;
        /* The cart as it is, its Avalon month and its codes included, then the same cart with what is offered, so
           every number is what this cart would pay (with the month, an edition can cost less than on its own).
           "For" is what the editions come to; "more" is what the whole cart goes up by. If Shopify's answer for the
           cart as it is does not match the cart's own total, nothing is said rather than a number that may be wrong. */
        const base = cartLines(cart);
        const codes = (cart.discount_codes || []).filter((d) => d && d.code && d.applicable !== false).map((d) => d.code);
        const plus = (extra) => [...base, ...extra.map((e) => ({ id: e.id, quantity: 1 }))];
        const asks = [ask(base, cfg, codes), ask(plus(missing), cfg, codes)];
        if (count <= 3) asks.push(ask(plus([missing[0]]), cfg, codes));
        const [now, archive, next] = await Promise.all(asks);
        if (Math.abs(now.total - Number(cart.total_price || 0) / 100) > 0.02) throw new Error('The cart could not be priced again.');
        const ids = new Set(cfg.editions.map((e) => e.id));
        const editionsIn = (q) => (q.lines || []).filter((line) => ids.has(line.id)).reduce((sum, line) => sum + line.total, 0);
        /* The same editions at their single prices: a rounded figure never goes above it. */
        const editionsListed = (q) => (q.lines || []).filter((line) => ids.has(line.id)).reduce((sum, line) => sum + line.subtotal, 0);
        const editionsShown = (q) => rule(q).upTo(editionsIn(q), editionsListed(q));
        const total = cfg.editions.length;
        const archiveLine = `the whole archive, all ${word(total)} for ${money(editionsShown(archive), archive.currency)}`;
        if (next) {
          const more = rule(next).upTo(Math.max(0, next.total - now.total), Math.max(0, next.original - now.original));
          lead.textContent = `A ${nth(count + 1)} edition makes it ${word(count + 1)} for ${money(editionsShown(next), next.currency)}, ${money(more, next.currency)} more.`;
          sub.textContent = `Or ${archiveLine}.`;
        } else {
          const change = archive.total - now.total;
          const delta = change > 0.005 ? rule(archive).upTo(change, Math.max(0, archive.original - now.original)) : change < -0.005 ? -rule(archive).down(-change) : 0;
          lead.textContent = `${archiveLine.charAt(0).toUpperCase()}${archiveLine.slice(1)}.`;
          sub.textContent = delta > 0.005
            ? `${money(delta, archive.currency)} more for the ${missing.length === 1 ? 'last one' : `other ${word(missing.length)}`}.`
            : delta < -0.005
              ? `${money(-delta, archive.currency)} less than what is in your cart now.`
              : 'The same as what is in your cart now.';
        }
        el.hidden = false;
      } catch (err) {
        el.hidden = true;
      }
    });
  };

  const init = () => {
    document.querySelectorAll('[data-av-archive]:not([data-av-ready])').forEach((el) => { el.dataset.avReady = '1'; initRow(el); });
    document.querySelectorAll('[data-av-next]:not([data-av-ready])').forEach((el) => { el.dataset.avReady = '1'; initNext(el); });
  };
  window.AvalonArchive = { init, take };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
  /* The cart page draws its sections again after a change: a new block is set up as it arrives. */
  new MutationObserver((records) => {
    if (records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1 &&
      (node.matches('[data-av-archive], [data-av-next]') || node.querySelector('[data-av-archive], [data-av-next]'))))) init();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
